import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Brain, Layers, Image as ImageIcon, Database, BookOpen, ExternalLink } from 'lucide-react';
import { Button } from '../../../../../components/ui/Button';
import { Badge } from '../../../../../components/ui/Badge';
import { IssueRow, MODULE_CONFIG } from '../shared';
import type { UnifiedQuestionRow } from '../hooks/useGovernanceData';
interface QuestionDetailPanelProps {
  row: UnifiedQuestionRow;
  onOpenQuestionInBank?: (row: UnifiedQuestionRow) => void;
}
export function QuestionDetailPanel({
  row,
  onOpenQuestionInBank
}: QuestionDetailPanelProps) {
  const modConfig = MODULE_CONFIG[row.moduleCode] || {
    bgClass: 'bg-gray-500',
    textClass: 'text-gray-700'
  };
  return <div className="px-4 py-4 bg-gray-50 border-t border-gray-100" data-id="element-5419">
      <div className="mb-4 flex justify-end" data-id="element-5420">
        <Button variant="secondary" size="sm" onClick={() => onOpenQuestionInBank?.(row)} disabled={!onOpenQuestionInBank} data-id="element-5421">
          <ExternalLink className="w-4 h-4 mr-1.5" data-id="element-5422" />
          Open in Vragenbank
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5423">
        {/* Left Column: Pipeline Issues */}
        <div className="space-y-4" data-id="element-5424">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-5425">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-5426" /> Pipeline Issues
          </h4>

          {row.pipelineIssues.length === 0 ? <div className="p-4 bg-white rounded-lg border border-gray-200 text-center" data-id="element-5427">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" data-id="element-5428" />
              <p className="text-sm text-gray-600" data-id="element-5429">
                Geen pipeline issues gevonden.
              </p>
            </div> : <div className="space-y-2" data-id="element-5430">
              {row.pipelineIssues.map((issue, idx) => <IssueRow key={idx} issue={issue} data-id="element-5431" />)}
            </div>}
        </div>

        {/* Right Column: Metadata & Quality */}
        <div className="space-y-6" data-id="element-5432">
          {/* Quality & Metadata */}
          <div data-id="element-5433">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5434">
              <Brain className="w-3.5 h-3.5" data-id="element-5435" /> Kwaliteit & Metadata
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4" data-id="element-5436">
              <div className="grid grid-cols-2 gap-4" data-id="element-5437">
                <div data-id="element-5438">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5439">Cognitief Niveau</p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-5440">
                    Niveau {row.cognitiveLevel}
                  </p>
                </div>
                <div data-id="element-5441">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5442">Module & Tier</p>
                  <div className="flex items-center gap-1.5" data-id="element-5443">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${modConfig.bgClass} text-white`} data-id="element-5444">
                      {row.moduleCode}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-5445">
                      Tier {row.tier}
                    </span>
                  </div>
                </div>
                <div data-id="element-5446">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5447">Vraagtype</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-5448">
                    {row.questionType}
                  </p>
                </div>
                <div data-id="element-5449">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5450">Skill</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-5451">
                    {row.skill}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Seed Status */}
          <div data-id="element-5452">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5453">
              <Database className="w-3.5 h-3.5" data-id="element-5454" /> Seed Status
            </h4>

            {row.seedWarnings.length === 0 ? <div className="p-3 bg-white rounded-lg border border-gray-200 flex items-center gap-2" data-id="element-5455">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-5456" />
                <span className="text-sm text-gray-600" data-id="element-5457">
                  In sync met taxonomy
                </span>
              </div> : <div className="space-y-2" data-id="element-5458">
                {row.seedWarnings.map((warn, i) => <div key={i} className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800 font-mono" data-id="element-5459">
                    {warn}
                  </div>)}
              </div>}
          </div>

          {/* Visual Status */}
          <div data-id="element-5460">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5461">
              <ImageIcon className="w-3.5 h-3.5" data-id="element-5462" /> Visuele Hulpmiddelen
            </h4>

            <div className="p-3 bg-white rounded-lg border border-gray-200" data-id="element-5463">
              {row.hasVisualProblems ? <div className="flex items-start gap-2 text-red-700" data-id="element-5464">
                  <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5465" />
                  <span className="text-sm" data-id="element-5466">
                    Er zijn problemen met de visuals (zie pipeline issues).
                  </span>
                </div> : <div className="flex items-start gap-2 text-emerald-700" data-id="element-5467">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5468" />
                  <span className="text-sm" data-id="element-5469">
                    Visuals zijn geldig en correct geconfigureerd.
                  </span>
                </div>}
            </div>
          </div>

          {/* Pedagogisch Contract */}
          <div data-id="element-5470">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5471">
              <BookOpen className="w-3.5 h-3.5" data-id="element-5472" /> Pedagogisch Contract
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3" data-id="element-5473">
              {(() => {
              const pedagogyIssues = row.pipelineIssues.filter(i => i.domain === 'pedagogy');
              const hasPedagogyBlockers = pedagogyIssues.some(i => i.kind === 'blocker');
              const hasPedagogyCoaches = pedagogyIssues.some(i => i.kind === 'coach');
              return <>
                    <div className="grid grid-cols-2 gap-3" data-id="element-5474">
                      <div data-id="element-5475">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-5476">DOK Niveau</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-5477">
                          {row.pipelineIssues.some(i => i.code === 'SEED_DOK_MISSING') ? <span className="text-amber-600" data-id="element-5478">
                              Niet ingesteld
                            </span> : 'Ingesteld'}
                        </p>
                      </div>
                      <div data-id="element-5479">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-5480">
                          Misconceptie Tags
                        </p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-5481">
                          {pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_UNTAGGED') ? <span className="text-amber-600" data-id="element-5482">Ontbreekt</span> : pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_PARTIAL') ? <span className="text-amber-500" data-id="element-5483">Gedeeltelijk</span> : <span className="text-emerald-600" data-id="element-5484">Compleet</span>}
                        </p>
                      </div>
                    </div>
                    {pedagogyIssues.length === 0 ? <div className="flex items-center gap-2 pt-1" data-id="element-5485">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-5486" />
                        <span className="text-sm text-gray-600" data-id="element-5487">
                          Pedagogisch contract voldoet aan alle eisen.
                        </span>
                      </div> : <div className="flex items-center gap-2 pt-1" data-id="element-5488">
                        {hasPedagogyBlockers ? <XCircle className="w-4 h-4 text-red-500" data-id="element-5489" /> : <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-5490" />}
                        <span className="text-sm text-gray-600" data-id="element-5491">
                          {pedagogyIssues.length} pedagogische{' '}
                          {pedagogyIssues.length === 1 ? 'bevinding' : 'bevindingen'}
                          {hasPedagogyBlockers ? ' (blocker)' : hasPedagogyCoaches ? ' (verbeterpunt)' : ''}
                        </span>
                      </div>}
                  </>;
            })()}
            </div>
          </div>
        </div>
      </div>
    </div>;
}